import type { HTMLProps } from 'react';

import { ChevronDown } from '@remarkable/ark-icons';

import type { CountryData } from 'src/utils/countryList';
import { COUNTRIES, useCountryLabel } from 'src/utils/countryList';
import { useFlagImage } from 'src/utils/useFlagImage';

import { Trans } from '@lingui/react/macro';
import { cn } from '../utils/classNamesHelper';

interface CountryPickerProps {
  countries?: Array<CountryData>;
  hasError?: boolean;
  value?: string;
}

export function CountryPicker2({
  className = '',
  countries = COUNTRIES,
  disabled = false,
  hasError = false,
  value = '',
  ref,
  ...rest
}: CountryPickerProps & HTMLProps<HTMLSelectElement>) {
  const displayValue = countries.find((c) => c.value === value)?.label;
  const flagImage = useFlagImage(value);
  const getCountryLabel = useCountryLabel();

  return (
    <div className={cn('rounded-sm relative flex items-center', className)}>
      {value ? (
        <img
          className="pointer-events-none absolute ml-12 size-32 rounded-full bg-neutral-light-4"
          src={flagImage.data}
          alt={`${displayValue ?? 'Country'} flag`}
        />
      ) : (
        <div className="absolute ml-12 size-32 rounded-full bg-neutral-light-4" />
      )}
      <select
        data-cy="country-picker"
        className={cn(
          'rounded-sm h-48 w-full cursor-pointer appearance-none border bg-none p-12 pl-56',
          {
            'text-gray-500': !value,
            'cursor-wait': disabled,
            'border-feedback-red-500': hasError,
            'shadow-border-muted': !hasError,
          }
        )}
        disabled={disabled}
        value={value}
        ref={ref}
        {...rest}
      >
        <option value="" disabled hidden>
          <Trans>Select country</Trans>
        </option>
        {countries
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((option) => (
            <option
              key={option.value}
              value={option.value}
              aria-selected={value === option.value}
            >
              {getCountryLabel(option.value)}
            </option>
          ))}
      </select>
      <ChevronDown
        size={24}
        className="pointer-events-none absolute top-0 right-12 h-full text-default"
      />
    </div>
  );
}
